import type { ResourceDocument, SyncOperation } from '../domain/resources';
import { compareOperations, type LocalDatabase } from './local-database';

const DATABASE_NAME = 'jidelnickovac-local';
const DATABASE_VERSION = 1;

function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error('IndexedDB request failed'));
  });
}

function transactionDone(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error || new Error('IndexedDB transaction failed'));
    transaction.onabort = () => reject(transaction.error || new Error('IndexedDB transaction aborted'));
  });
}

export class IndexedDbAdapter implements LocalDatabase {
  readonly kind = 'indexeddb' as const;
  private database: IDBDatabase | null = null;

  async initialize(): Promise<void> {
    if (!globalThis.indexedDB) throw new Error('IndexedDB is not available');
    const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains('resources')) {
        database.createObjectStore('resources', { keyPath: 'type' });
      }
      if (!database.objectStoreNames.contains('outbox')) {
        const outbox = database.createObjectStore('outbox', { keyPath: 'id' });
        outbox.createIndex('createdAt', 'createdAt');
      }
      if (!database.objectStoreNames.contains('metadata')) {
        database.createObjectStore('metadata', { keyPath: 'key' });
      }
    };
    this.database = await requestResult(request);
  }

  async listResources(): Promise<ResourceDocument[]> {
    const store = this.store('resources', 'readonly');
    return requestResult(store.getAll()) as Promise<ResourceDocument[]>;
  }

  async putResource(resource: ResourceDocument): Promise<void> {
    const transaction = this.transaction('resources', 'readwrite');
    transaction.objectStore('resources').put(structuredClone(resource));
    await transactionDone(transaction);
  }

  async commit(resource: ResourceDocument, operations: SyncOperation[]): Promise<void> {
    const transaction = this.transaction(['resources', 'outbox'], 'readwrite');
    transaction.objectStore('resources').put(structuredClone(resource));
    const outbox = transaction.objectStore('outbox');
    operations.forEach(operation => outbox.put(structuredClone(operation)));
    await transactionDone(transaction);
  }

  async listOperations(): Promise<SyncOperation[]> {
    const store = this.store('outbox', 'readonly');
    const rows = await requestResult(store.getAll()) as SyncOperation[];
    return rows.sort(compareOperations);
  }

  async putOperations(operations: SyncOperation[]): Promise<void> {
    if (operations.length === 0) return;
    const transaction = this.transaction('outbox', 'readwrite');
    const store = transaction.objectStore('outbox');
    operations.forEach(operation => store.put(structuredClone(operation)));
    await transactionDone(transaction);
  }

  async deleteOperations(ids: string[]): Promise<void> {
    if (ids.length === 0) return;
    const transaction = this.transaction('outbox', 'readwrite');
    const store = transaction.objectStore('outbox');
    ids.forEach(id => store.delete(id));
    await transactionDone(transaction);
  }

  async getMetadata(key: string): Promise<string | null> {
    const row = await requestResult(this.store('metadata', 'readonly').get(key)) as
      | { key: string; value: string }
      | undefined;
    return row?.value ?? null;
  }

  async setMetadata(key: string, value: string): Promise<void> {
    const transaction = this.transaction('metadata', 'readwrite');
    transaction.objectStore('metadata').put({ key, value });
    await transactionDone(transaction);
  }

  async flush(): Promise<void> {}

  private transaction(store: string | string[], mode: IDBTransactionMode): IDBTransaction {
    if (!this.database) throw new Error('IndexedDB adapter is not initialized');
    return this.database.transaction(store, mode);
  }

  private store(name: string, mode: IDBTransactionMode): IDBObjectStore {
    return this.transaction(name, mode).objectStore(name);
  }
}
